import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaBullseye, FaShieldAlt, FaDatabase, FaBell, FaDownload, FaExclamationTriangle } from 'react-icons/fa';
import { CSVLink } from 'react-csv';
import { useSettings, useTransactions, clearCollection } from '../hooks/useFirestore';
import { doc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

const Settings = () => {
    const { settings, loading, updateSettings } = useSettings();
    const { data: allTransactions } = useTransactions();
    const [localSettings, setLocalSettings] = useState(null);
    const [showReset, setShowReset] = useState(false);
    const [resetting, setResetting] = useState(false);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (settings && !localSettings) {
            setLocalSettings({ ...settings });
        }
    }, [settings, localSettings]);

    const debouncedSave = useCallback((data) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            updateSettings(data);
        }, 800);
    }, [updateSettings]);

    const handleChange = (key, value) => {
        const updated = { ...localSettings, [key]: value };
        setLocalSettings(updated);
        debouncedSave({ [key]: value });
    };

    const handleCategoryBudget = (cat, value) => {
        const budgets = { ...(localSettings?.categoryBudgets || {}), [cat]: Number(value) || 0 };
        const updated = { ...localSettings, categoryBudgets: budgets };
        setLocalSettings(updated);
        debouncedSave({ categoryBudgets: budgets });
    };

    const handleToggle = (key) => {
        const toggles = { ...(localSettings?.toggles || {}), [key]: !(localSettings?.toggles?.[key]) };
        const updated = { ...localSettings, toggles };
        setLocalSettings(updated);
        debouncedSave({ toggles });
    };

    const handleFactoryReset = async () => {
        setResetting(true);
        try {
            const collections = ['transactions', 'goals', 'assets', 'liabilities', 'emis', 'vault', 'watchlist', 'orders', 'netWorthHistory', 'trades', 'fixedExpenses', 'operationalExpenses', 'spendingTargets', 'gamification'];
            for (const col of collections) {
                await clearCollection(col);
            }
            const user = auth.currentUser;
            if (user) {
                const ref = doc(db, 'settings', user.uid);
                await deleteDoc(ref);
            }
            setLocalSettings(null);
            setShowReset(false);
            window.location.reload();
        } catch (err) {
            console.error('Factory reset error:', err);
        } finally {
            setResetting(false);
        }
    };

    const csvData = (allTransactions || []).map(t => ({
        Date: t.date, Description: t.description, Amount: t.amount,
        Type: t.type, Category: t.category || '', Balance: t.balance || '',
    }));

    if (loading || !localSettings) {
        return <div className="flex items-center justify-center h-64"><div className="text-frost-400 text-sm">Loading settings...</div></div>;
    }

    const categories = Object.keys(localSettings?.categoryBudgets || {
        Food: 15000, Shopping: 10000, Transport: 5000, Entertainment: 5000,
        Bills: 20000, Health: 5000, Education: 10000, Other: 10000,
    });

    return (
        <div className="space-y-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <p className="text-[10px] font-semibold text-accent-400 uppercase tracking-[0.2em] mb-1">Configuration</p>
                    <h1 className="text-2xl font-bold tracking-tight text-frost-900">System Settings</h1>
                    <p className="text-frost-500 mt-0.5 text-sm">Configure targets, security and parameters</p>
                </div>
                <div className="flex items-center gap-2.5 bg-mint-500/10 border border-mint-500/20 px-4 py-2 rounded-lg">
                    <div className="w-1.5 h-1.5 rounded-full bg-mint-500 animate-pulse-soft"></div>
                    <span className="text-[10px] font-semibold text-mint-400 uppercase tracking-widest">Auto-saving</span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Financial Targets */}
                <div className="glass-card p-8 space-y-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-accent-500/5 blur-[80px] rounded-full"></div>
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="p-3.5 bg-accent-500/10 rounded-xl text-accent-400 border border-accent-500/20">
                            <FaBullseye className="text-lg" />
                        </div>
                        <h3 className="text-lg font-bold tracking-tight text-frost-900">Financial Targets</h3>
                    </div>
                    <div className="space-y-5 relative z-10">
                        <TargetInput label="Monthly Cumulative Inflow" value={localSettings.monthlyIncomeTarget || 0}
                            onChange={(v) => handleChange('monthlyIncomeTarget', Number(v))} />
                        <TargetInput label="Primary Strategic Reserve" value={localSettings.savingsTarget || 0}
                            onChange={(v) => handleChange('savingsTarget', Number(v))} />
                        <TargetInput label="Shopping Hardware Buffer" value={localSettings.shoppingCap || 0}
                            onChange={(v) => handleChange('shoppingCap', Number(v))} />
                        <TargetInput label="Lifestyle & Subsistence" value={localSettings.lifestyleCap || 0}
                            onChange={(v) => handleChange('lifestyleCap', Number(v))} />
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Privacy & Security */}
                    <div className="glass-card p-6 relative overflow-hidden">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="p-3.5 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl text-white shadow-glow">
                                <FaShieldAlt className="text-base" />
                            </div>
                            <h3 className="text-base font-bold tracking-tight text-frost-900">Privacy & Security</h3>
                        </div>
                        <div className="space-y-5">
                            <ToggleSetting label="Biometric Prompt" description="Request auth on every session start"
                                checked={localSettings?.toggles?.biometricPrompt} onChange={() => handleToggle('biometricPrompt')} />
                            <ToggleSetting label="Auto-Lock Vault" description="Lock vault after 300s of inactivity"
                                checked={localSettings?.toggles?.autoLockVault} onChange={() => handleToggle('autoLockVault')} />
                            <ToggleSetting label="Cloud Backups" description="Sync encrypted data to private cloud"
                                checked={localSettings?.toggles?.cloudBackups} onChange={() => handleToggle('cloudBackups')} />
                        </div>
                    </div>

                    {/* Intelligence Alerts */}
                    <div className="glass-card p-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="p-3.5 bg-gold-500/10 rounded-xl text-gold-400 border border-gold-500/20">
                                <FaBell className="text-base" />
                            </div>
                            <h3 className="text-base font-bold tracking-tight text-frost-900">Intelligence Alerts</h3>
                        </div>
                        <div className="space-y-5">
                            <ToggleSetting label="Pacing Alerts" description="Alert when target trajectory is breached"
                                checked={localSettings?.toggles?.pacingAlerts} onChange={() => handleToggle('pacingAlerts')} />
                            <ToggleSetting label="Anomalous Spend Detection" description="Trigger for category cap violations"
                                checked={localSettings?.toggles?.anomalyDetection} onChange={() => handleToggle('anomalyDetection')} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Category Budget Limits */}
            <div className="glass-card p-8 space-y-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-mint-500/5 blur-[80px] rounded-full"></div>
                <h3 className="text-lg font-bold tracking-tight text-frost-900 relative z-10">Category Budget Limits</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 relative z-10">
                    {categories.map(cat => (
                        <div key={cat} className="space-y-1.5">
                            <label className="text-[10px] font-medium text-frost-400 uppercase tracking-widest">{cat}</label>
                            <input
                                type="number" value={localSettings?.categoryBudgets?.[cat] || 0}
                                onChange={(e) => handleCategoryBudget(cat, e.target.value)}
                                className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-frost-900 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500/30 transition-all"
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Export & Danger Zone */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="glass-card p-8 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-5">
                        <div className="p-4 bg-accent-500/10 rounded-xl text-accent-400 text-xl border border-accent-500/20">
                            <FaDownload />
                        </div>
                        <div>
                            <h4 className="font-bold text-frost-900 text-base tracking-tight mb-1">Export Data</h4>
                            <p className="text-sm text-frost-500">Download all transactions as CSV</p>
                        </div>
                    </div>
                    <CSVLink data={csvData} filename={`transactions-${selectedMonthLabel()}.csv`}
                        className="btn-pill bg-accent-500/10 text-accent-400 border border-accent-500/20 hover:bg-accent-500/20 text-xs font-semibold uppercase tracking-wider">
                        Download CSV
                    </CSVLink>
                </div>

                <div className="glass-card p-8 flex flex-col md:flex-row items-center justify-between gap-6 border-gold-500/10 group">
                    <div className="flex items-center gap-5">
                        <div className="p-4 bg-gold-500/10 rounded-xl text-gold-400 text-xl group-hover:scale-105 transition-transform duration-500 border border-gold-500/20">
                            <FaDatabase />
                        </div>
                        <div>
                            <h4 className="font-bold text-frost-900 text-base tracking-tight mb-1">Danger Zone</h4>
                            <p className="text-sm text-frost-500 max-w-md">Permanently purge all data from the portal.</p>
                        </div>
                    </div>
                    <button onClick={() => setShowReset(true)}
                        className="btn-pill bg-gold-500/10 text-gold-400 border border-gold-500/20 hover:bg-gold-500/20 text-xs font-semibold uppercase tracking-wider">
                        Factory Reset
                    </button>
                </div>
            </div>

            {/* Factory Reset Confirmation Modal */}
            {showReset && (
                <div className="fixed inset-0 z-[200] bg-dark-700/80 backdrop-blur-xl flex items-center justify-center p-6">
                    <div className="bg-dark-500 w-full max-w-md rounded-2xl p-8 shadow-card-hover border border-white/[0.06] space-y-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-gold-500/10 rounded-xl text-gold-400">
                                <FaExclamationTriangle className="text-xl" />
                            </div>
                            <h3 className="text-lg font-bold text-frost-900">Confirm Factory Reset</h3>
                        </div>
                        <p className="text-sm text-frost-500">This will permanently delete ALL your data including transactions, goals, vault secrets, assets, orders, and settings. This cannot be undone.</p>
                        <div className="flex gap-3">
                            <button onClick={() => setShowReset(false)}
                                className="flex-1 py-3 border border-white/[0.06] text-frost-500 font-bold rounded-xl hover:bg-white/[0.03] transition-all text-sm">
                                Cancel
                            </button>
                            <button onClick={handleFactoryReset} disabled={resetting}
                                className="flex-1 py-3 bg-gold-500/20 text-gold-400 font-bold rounded-xl hover:bg-gold-500/30 transition-all text-sm border border-gold-500/20">
                                {resetting ? 'Resetting...' : 'Delete Everything'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function selectedMonthLabel() {
    return new Date().toISOString().slice(0, 7);
}

const TargetInput = ({ label, value, onChange }) => (
    <div className="space-y-1.5 group">
        <label className="text-[10px] font-medium text-frost-400 uppercase tracking-widest">{label}</label>
        <input type="number" value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3.5 text-frost-900 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500/30 transition-all placeholder:text-frost-400"
        />
    </div>
);

const ToggleSetting = ({ label, description, checked, onChange }) => (
    <div className="flex items-center justify-between group cursor-pointer" onClick={onChange}>
        <div className="flex-1 pr-6">
            <h4 className="text-sm font-semibold text-frost-800 group-hover:text-accent-300 transition-colors">{label}</h4>
            <p className="text-[11px] text-frost-500 mt-0.5">{description}</p>
        </div>
        <div className={`w-10 h-5.5 rounded-full relative transition-all duration-500 p-0.5 ${checked ? 'bg-accent-500 shadow-glow' : 'bg-white/[0.06]'}`}>
            <div className={`absolute top-0.5 w-4.5 h-4.5 bg-white rounded-full transition-all duration-500 shadow-sm ${checked ? 'translate-x-[18px]' : 'translate-x-0.5'}`} style={{width: '18px', height: '18px'}}></div>
        </div>
    </div>
);

export default Settings;
